import React, { useEffect } from "react";
import { CircularProgress } from "@mui/material";

// ─── shared badge helpers (used only in modal) ────────────────────────────────

export const PlatformBadge = ({ platform }) => {
  const p = {
    android: { bg: "#e8f5e9", text: "#2e7d32", dot: "#43a047" },
    ios:     { bg: "#e3f2fd", text: "#1565c0", dot: "#1e88e5" },
    web:     { bg: "#fce4ec", text: "#880e4f", dot: "#e91e63" },
    default: { bg: "#f3e5f5", text: "#6a1b9a", dot: "#ab47bc" },
  };
  const c = p[platform?.toLowerCase()] || p.default;
  return (
    <span style={{
      background: c.bg, color: c.text, border: `1px solid ${c.dot}33`,
      borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 600,
      display: "inline-flex", alignItems: "center", gap: 5,
      fontFamily: "'DM Mono', monospace",
    }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: c.dot, display: "inline-block" }} />
      {platform || "—"}
    </span>
  );
};

export const UserTypePill = ({ type }) => {
  const p = {
    retailer:    { bg: "#fff8e1", text: "#f57f17", border: "#ffe08233" },
    distributor: { bg: "#e8eaf6", text: "#283593", border: "#7986cb33" },
    default:     { bg: "#f1f8e9", text: "#33691e", border: "#aed58133" },
  };
  const c = p[type?.toLowerCase()] || p.default;
  return (
    <span style={{
      background: c.bg, color: c.text, border: `1px solid ${c.border}`,
      borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 700,
      fontFamily: "'DM Mono', monospace", textTransform: "capitalize",
    }}>
      {type || "—"}
    </span>
  );
};

export const NetworkBadge = ({ network }) => {
  const n = network?.toLowerCase();
  const color = n === "wifi" ? "#0284c7" : n === "5g" ? "#7c3aed" : n === "4g" ? "#059669" : "#6b7280";
  return (
    <span style={{
      background: color + "15", color, border: `1px solid ${color}30`,
      borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700,
      fontFamily: "'DM Mono', monospace", letterSpacing: 0.5,
    }}>
      {network || "—"}
    </span>
  );
};

// ─── helpers ──────────────────────────────────────────────────────────────────

export function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
  });
}

export function sessionDuration(start, end) {
  if (!start || !end) return "—";
  const diff = Math.max(0, new Date(end) - new Date(start));
  const m = Math.floor(diff / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  if (m === 0 && s === 0) return "< 1s";
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

// ─── session detail modal ─────────────────────────────────────────────────────

const SessionDetailModal = ({ user, sessions, isLoading, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(15,23,42,0.55)",
        backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 20,
          width: "100%", maxWidth: 780,
          maxHeight: "90vh", display: "flex", flexDirection: "column",
          boxShadow: "0 25px 60px rgba(0,0,0,0.25)",
          overflow: "hidden",
          fontFamily: "'Plus Jakarta Sans','Segoe UI',sans-serif",
        }}
      >
        {/* ── modal header — green gradient ── */}
        <div style={{
          background: "linear-gradient(135deg, #064e3b 0%, #059669 55%, #34d399 100%)",
          padding: "20px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "flex-start",
          flexShrink: 0,
        }}>
          <div>
            {/* avatar + name row */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
              <div style={{
                width: 46, height: 46, borderRadius: "50%",
                background: "rgba(255,255,255,0.22)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 20, fontWeight: 800, color: "#fff",
                border: "2px solid rgba(255,255,255,0.4)",
                flexShrink: 0,
              }}>
                {(user?.user_name || "U").charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{ color: "#fff", fontWeight: 800, fontSize: 17, lineHeight: 1.25 }}>
                  {user?.user_name || "—"}
                </div>
                <div style={{
                  color: "rgba(255,255,255,0.78)", fontSize: 12,
                  fontFamily: "'DM Mono',monospace", marginTop: 2,
                }}>
                  {user?.user_id}
                </div>
              </div>
            </div>

            {/* meta chips */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {[
                { icon: "📱", label: user?.mobile },
                { icon: "📍", label: user?.state },
                { icon: "🔁", label: `${user?.total_sessions_count} sessions` },
              ].map(({ icon, label }) => label && (
                <span key={label} style={{
                  background: "rgba(255,255,255,0.18)", color: "#fff",
                  borderRadius: 20, padding: "3px 11px", fontSize: 12,
                  border: "1px solid rgba(255,255,255,0.28)",
                  display: "inline-flex", alignItems: "center", gap: 5,
                }}>
                  {icon} {label}
                </span>
              ))}
            </div>
          </div>

          {/* close button */}
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: "rgba(255,255,255,0.18)",
              border: "1px solid rgba(255,255,255,0.3)",
              color: "#fff", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 20, lineHeight: 1, flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>

        {/* ── section label ── */}
        <div style={{
          padding: "14px 24px 0",
          display: "flex", alignItems: "center", gap: 8,
          flexShrink: 0,
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: 8,
            background: "linear-gradient(135deg,#059669,#047857)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <span style={{ fontWeight: 700, fontSize: 13, color: "#374151" }}>Session History</span>
          {!isLoading && (
            <span style={{
              background: "#d1fae5", color: "#065f46", border: "1px solid #a7f3d0",
              borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700,
              fontFamily: "'DM Mono',monospace", marginLeft: 4,
            }}>
              {sessions.length} sessions
            </span>
          )}
        </div>

        {/* ── scrollable body ── */}
        <div style={{ overflowY: "auto", padding: "12px 24px 24px", flexGrow: 1 }}>
          {isLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
              <CircularProgress size={32} style={{ color: "#059669" }} />
            </div>
          ) : sessions.length === 0 ? (
            <div style={{ textAlign: "center", padding: 48, color: "#9ca3af", fontSize: 14 }}>
              No session records found.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {sessions.map((s, idx) => (
                <div key={s.id} style={{
                  border: "1px solid #e8ecf0", borderRadius: 14,
                  overflow: "hidden",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                }}>
                  {/* session card header */}
                  <div style={{
                    background: "linear-gradient(90deg,#f0fdf4,#ecfdf5)",
                    padding: "10px 16px",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    borderBottom: "1px solid #d1fae5",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{
                        width: 24, height: 24, borderRadius: "50%",
                        background: "linear-gradient(135deg,#059669,#047857)",
                        color: "#fff", fontSize: 11, fontWeight: 800,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                      }}>
                        {idx + 1}
                      </span>
                      {/* <span style={{ fontWeight: 700, fontSize: 13, color: "#374151" }}>
                        Session #{s.id}
                      </span> */}
                      <UserTypePill type={s.user_type} />
                      <PlatformBadge platform={s.platform} />
                    </div>

                    {/* duration pill */}
                    <span style={{
                      background: "#f0fdf4", color: "#15803d",
                      border: "1px solid #bbf7d0", borderRadius: 20,
                      padding: "3px 10px", fontSize: 11, fontWeight: 700,
                      fontFamily: "'DM Mono',monospace",
                      display: "flex", alignItems: "center", gap: 4,
                    }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#15803d" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                      </svg>
                      {sessionDuration(s.started_at, s.ended_at)}
                    </span>
                  </div>

                  {/* session card body — 2 col grid */}
                  <div style={{
                    padding: "12px 16px",
                    display: "grid", gridTemplateColumns: "1fr 1fr",
                    gap: "10px 20px",
                  }}>
                    {/* device info */}
                    <div style={{
                      background: "#fafbfc", borderRadius: 10, padding: "10px 14px",
                      border: "1px solid #f1f5f9",
                    }}>
                      <div style={{
                        fontSize: 10, fontWeight: 700, color: "#9ca3af",
                        textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8,
                      }}>
                        Device Info
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                        {[
                          { label: "Model",       value: s.device_info?.model },
                          { label: "OS Version",  value: s.device_info?.os_version },
                          { label: "App Version", value: s.device_info?.app_version },
                          { label: "Network",     value: s.device_info?.network, isNetwork: true },
                        ].map(({ label, value, isNetwork }) => (
                          <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontSize: 11, color: "#6b7280" }}>{label}</span>
                            {isNetwork
                              ? <NetworkBadge network={value} />
                              : <span style={{ fontSize: 12, fontWeight: 600, color: "#374151", fontFamily: "'DM Mono',monospace" }}>{value || "—"}</span>
                            }
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* timing info */}
                    <div style={{
                      background: "#fafbfc", borderRadius: 10, padding: "10px 14px",
                      border: "1px solid #f1f5f9",
                    }}>
                      <div style={{
                        fontSize: 10, fontWeight: 700, color: "#9ca3af",
                        textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8,
                      }}>
                        Timing
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                        {[
                          { label: "Started At", value: formatDate(s.started_at), dot: "#10b981" },
                          { label: "Ended At",   value: formatDate(s.ended_at),   dot: "#ef4444" },
                          { label: "Created At", value: formatDate(s.created_at), dot: "#6b7280" },
                        ].map(({ label, value, dot }) => (
                          <div key={label}>
                            <div style={{ fontSize: 10, color: "#9ca3af", marginBottom: 1 }}>{label}</div>
                            <div style={{
                              fontSize: 11, fontWeight: 600, color: "#374151",
                              fontFamily: "'DM Mono',monospace",
                              display: "flex", alignItems: "center", gap: 5,
                            }}>
                              <span style={{ width: 6, height: 6, borderRadius: "50%", background: dot, display: "inline-block", flexShrink: 0 }} />
                              {value}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionDetailModal;
